import {
  collection,
  addDoc,
  updateDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create a new catalog part. currentStock always starts at 0 — stock only
 * ever increases through a real "receive stock" entry, never by typing a
 * number directly, so there's always an entry record backing every unit.
 */
export const createSparePart = async ({ name, code, category, unitCost, reorderLevel, createdBy }) => {
  const part = {
    name: name.trim(),
    code: (code || '').trim(),
    category,
    unitCost: parseFloat(unitCost) || 0,
    reorderLevel: parseInt(reorderLevel) || 0,
    currentStock: 0,
    active: true,
    createdBy: createdBy.id,
    createdByName: createdBy.name,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };
  const docRef = await addDoc(collection(db, 'spareParts'), part);
  return { id: docRef.id, ...part };
};

export const listCatalog = async () => {
  const snap = await getDocs(query(collection(db, 'spareParts'), orderBy('name')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Record stock received into inventory, and atomically bump the part's
 * currentStock — a transaction so the two writes can't drift apart even
 * if two people log a delivery at the same moment.
 */
export const receiveStock = async ({ sparePart, quantity, unitCost, supplier, notes, receivedBy }) => {
  const qty = parseInt(quantity);
  if (!qty || qty <= 0) throw new Error('Quantity must be a positive number');

  const partRef = doc(db, 'spareParts', sparePart.id);

  await runTransaction(db, async (transaction) => {
    const partSnap = await transaction.get(partRef);
    if (!partSnap.exists()) throw new Error('Spare part no longer exists');

    const currentStock = partSnap.data().currentStock || 0;

    const entryRef = doc(collection(db, 'sparePartEntries'));
    transaction.set(entryRef, {
      sparePartId: sparePart.id,
      sparePartName: sparePart.name,
      category: sparePart.category,
      quantity: qty,
      unitCost: parseFloat(unitCost) || sparePart.unitCost || 0,
      totalCost: (parseFloat(unitCost) || sparePart.unitCost || 0) * qty,
      supplier: (supplier || '').trim(),
      notes: (notes || '').trim(),
      receivedBy: receivedBy.id,
      receivedByName: receivedBy.name,
      receivedAt: serverTimestamp()
    });

    transaction.update(partRef, {
      currentStock: currentStock + qty,
      updatedAt: serverTimestamp()
    });
  });
};

/**
 * Issue stock out to a technician, atomically decrementing currentStock.
 * Refuses to issue more than is actually in stock.
 */
export const issueStock = async ({ sparePart, quantity, technician, notes, issuedBy }) => {
  const qty = parseInt(quantity);
  if (!qty || qty <= 0) throw new Error('Quantity must be a positive number');

  const partRef = doc(db, 'spareParts', sparePart.id);

  await runTransaction(db, async (transaction) => {
    const partSnap = await transaction.get(partRef);
    if (!partSnap.exists()) throw new Error('Spare part no longer exists');

    const currentStock = partSnap.data().currentStock || 0;
    if (qty > currentStock) {
      throw new Error(`Only ${currentStock} in stock — can't issue ${qty}`);
    }

    const issueRef = doc(collection(db, 'sparePartIssues'));
    transaction.set(issueRef, {
      sparePartId: sparePart.id,
      sparePartName: sparePart.name,
      category: sparePart.category,
      quantity: qty,
      issuedTo: technician.id,
      issuedToName: technician.name,
      issuedBy: issuedBy.id,
      issuedByName: issuedBy.name,
      notes: (notes || '').trim(),
      issuedAt: serverTimestamp()
    });

    transaction.update(partRef, {
      currentStock: currentStock - qty,
      updatedAt: serverTimestamp()
    });
  });
};

export const listEntries = async () => {
  const snap = await getDocs(query(collection(db, 'sparePartEntries'), orderBy('receivedAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const listIssues = async () => {
  const snap = await getDocs(query(collection(db, 'sparePartIssues'), orderBy('issuedAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const listIssuesForTechnician = async (technicianId) => {
  const q = query(
    collection(db, 'sparePartIssues'),
    where('issuedTo', '==', technicianId),
    orderBy('issuedAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};
